import { useStore } from '@vromo/mobx-react';
import { Campaign, EntityList } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { useModals } from '@vromo/ui/components/Modals';
import Title from '@vromo/ui/components/Title';
import { useToasts } from '@vromo/ui/components/Toasts';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { update } from 'serializr';
import { qaLabelCampaigns } from '../../../../common-qa-labels/campaigns';
import { RootStore } from '../stores/RootStore';
import CampaignCard from './CampaignCard';
import styles, { CardContainer } from './campaigns.style';
import NewCampaign from './NewCampaign';
import { useTranslation } from 'react-i18next';

const Campaigns = observer(() => {
    const [newOpen, setNewOpen] = useState<boolean>(false);
    const {
        authStore: { graph, role },
        campaignStore
    } = useStore<RootStore>('rootStore');

    const { t } = useTranslation();
    const modals = useModals();
    const showToast = useToasts();

    const createNewCampaign = async (campaign: Campaign) => {
        try {
            const campaigns = new EntityList(Campaign);
            campaigns.push(campaign);

            const response = await graph.root.graph().roles(role.id).campaigns().post(campaigns);

            runInAction(() => {
                setNewOpen(false);
                update(campaignStore.campaigns, response);
                showToast(`Successfully created campaign ${campaign.name}`, 'success');
            });
        } catch (e) {
            console.error(e);
            setNewOpen(false);
            showToast(t('campaign.errorCreating'), 'error');
        }
    };

    const deleteCampaign = async (campaign: Campaign) => {
        if (!campaign.id) return;
        const hasConfirmedDeletion = await modals.confirm(
            'Delete Campaign',
            'Are you sure you want to delete this campaign?'
        );
        if (!hasConfirmedDeletion) return;

        try {
            await graph.root.graph().roles(role.id).campaigns(campaign.id).delete();

            runInAction(() => {
                setNewOpen(false);
                showToast(`Removed campaign ${campaign.name}`);
                campaignStore.campaigns.items.remove(campaign);
            });
        } catch (e) {
            console.error(e);
            showToast(t('campaign.errorDeleting'), 'error');
        }
    };

    return (
        <>
            <div style={styles.topContent}>
                <Title>{t('campaign.campaigns')}</Title>
                <Button
                    onClick={() => setNewOpen(true)}
                    data-testid={qaLabelCampaigns.addCampaignButton}>
                    {t('campaign.addNewCampaign')}
                </Button>
            </div>
            {campaignStore.loaded ? (
                <CardContainer>
                    {!!campaignStore.campaigns.length ? (
                        campaignStore.campaigns.map((campaign) => (
                            <CampaignCard
                                key={campaign.id}
                                campaign={campaign}
                                deleteCampaign={() => deleteCampaign(campaign)}
                            />
                        ))
                    ) : (
                        <i>{t('campaign.newCampaign')}</i>
                    )}
                </CardContainer>
            ) : (
                <div style={styles.loadingContainer}>
                    <FontAwesome icon="spinner-third" x3 spin />
                    <div style={styles.loadingText}>{t('campaign.loading')}</div>
                </div>
            )}
            <NewCampaign
                isOpen={newOpen}
                create={createNewCampaign}
                onCancel={() => setNewOpen(false)}
            />
        </>
    );
});

export default Campaigns;
